'use client';

import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { TransparentAwareImage } from '@/components/ui/media/transparent-aware-image';
import { useMemo } from 'react';
import type {
  WorkspaceReferenceImage,
  WorkspaceReferenceMedia,
} from '../contracts/runtime-state';
import { useWorkspaceImageMentionEditor } from './use-workspace-image-mention-editor';
import { useWorkspaceImageMentionMenuAnchor } from './use-workspace-image-mention-menu-anchor';
import { useWorkspacePromptComposerSize } from './use-workspace-prompt-composer-size';
import { useWorkspacePromptPolish } from './use-workspace-prompt-polish';

export function WorkspaceImageMentionInput({
  ariaLabel,
  placeholder,
  prompt,
  promptRequired,
  references,
  referenceMedia = [],
  polishLabel = 'Polish',
  polishUndoLabel = 'Undo',
  polishSucceededLabel = 'Prompt polished.',
  polishFailedLabel = 'Prompt polish failed. Please try again.',
  onPolishingChange,
  onPromptChange,
}: {
  ariaLabel: string;
  placeholder: string;
  prompt: string;
  promptRequired: boolean;
  references: WorkspaceReferenceImage[];
  referenceMedia?: WorkspaceReferenceMedia[];
  polishLabel?: string;
  polishUndoLabel?: string;
  polishSucceededLabel?: string;
  polishFailedLabel?: string;
  onPolishingChange?: (isPolishing: boolean) => void;
  onPromptChange: (value: string) => void;
}) {
  const images = useMemo(
    () => references.filter((image) => !image.isEmpty),
    [references]
  );
  const polish = useWorkspacePromptPolish({
    prompt,
    images,
    polishUndoLabel,
    polishSucceededLabel,
    polishFailedLabel,
    onPromptChange,
    onPolishingChange,
  });
  const editor = useWorkspaceImageMentionEditor({
    prompt,
    images,
    media: referenceMedia,
    disabled: polish.isPolishing,
    onPromptChange,
  });
  const menuRef = useWorkspaceImageMentionMenuAnchor({
    rootRef: editor.rootRef,
    editorRef: editor.editorRef,
    activeQuery: editor.activeRange,
    optionCount: editor.filteredReferences.length,
  });
  const composerSize = useWorkspacePromptComposerSize({
    editorRef: editor.editorRef,
    prompt,
  });

  return (
    <div
      ref={editor.rootRef}
      className="cms-block-workspace-workspace-1-workspace-image-mention"
    >
      <div
        ref={editor.editorRef}
        className="cms-block-workspace-workspace-1-workspace-image-mention__editor"
        contentEditable={!polish.isPolishing}
        suppressContentEditableWarning
        role="textbox"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-multiline="true"
        aria-required={promptRequired}
        aria-busy={polish.isPolishing}
        aria-disabled={polish.isPolishing}
        data-placeholder={placeholder}
        data-polishing={polish.isPolishRequestPending ? 'true' : undefined}
        onInput={() => {
          editor.onInput();
          composerSize.scheduleHeight();
        }}
        onClick={editor.updateActiveQuery}
        onCompositionStart={editor.onCompositionStart}
        onCompositionEnd={editor.onCompositionEnd}
        onPaste={(event) => {
          event.preventDefault();
          editor.onPaste(event.clipboardData.getData('text/plain'));
        }}
        onKeyUp={(event) => {
          if (
            event.key !== 'ArrowDown' &&
            event.key !== 'ArrowUp' &&
            event.key !== 'Enter' &&
            event.key !== 'Escape'
          ) {
            editor.updateActiveQuery();
          }
        }}
        onKeyDown={editor.handleKeyDown}
      />

      <div className="cms-block-workspace-workspace-1-workspace-image-mention__footer">
        <div className="cms-block-workspace-workspace-1-workspace-image-mention__actions">
          <button
            type="button"
            className="cms-block-workspace-workspace-1-workspace-image-mention__trigger"
            aria-label="@"
            aria-expanded={Boolean(editor.activeRange)}
            disabled={
              polish.isPolishing || images.length + referenceMedia.length === 0
            }
            onMouseDown={(event) => event.preventDefault()}
            onClick={editor.openMentionMenu}
          >
            @
          </button>
          <button
            type="button"
            className="cms-block-workspace-workspace-1-workspace-image-mention__polish"
            aria-label={polishLabel}
            title={polishLabel}
            disabled={!polish.canPolish}
            data-loading={polish.isPolishing ? 'true' : undefined}
            onClick={() => {
              editor.closeMenu();
              void polish.handlePolish();
            }}
          >
            {renderAppIcon({
              name: 'translate-2-ai',
              className:
                'cms-block-workspace-workspace-1-workspace-image-mention__polishIcon',
            })}
            <span>{polishLabel}</span>
          </button>
        </div>
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          className="cms-block-workspace-workspace-1-workspace-image-mention__resizeHandle"
          {...composerSize.resizeHandleProps}
        />
      </div>

      {editor.activeRange && editor.filteredReferences.length > 0 ? (
        <div
          ref={menuRef}
          className="cms-block-workspace-workspace-1-workspace-image-mention__menu"
          aria-label={ariaLabel}
        >
          {editor.filteredReferences.map((item, index) => (
            <button
              key={`${item.reference.kind}-${item.reference.id}`}
              type="button"
              className="cms-block-workspace-workspace-1-workspace-image-mention__option"
              data-active={index === editor.activeOption ? 'true' : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => editor.selectReference(item.index)}
            >
              {item.reference.kind === 'image' && item.reference.previewUrl ? (
                <TransparentAwareImage
                  loadingSkeleton
                  src={item.reference.previewUrl}
                  alt=""
                  aria-hidden="true"
                  className="cms-block-workspace-workspace-1-workspace-image-mention__optionImage"
                  loading="eager"
                  showCheckerboard
                />
              ) : (
                renderAppIcon({
                  name: item.reference.kind === 'video' ? 'Video' : 'MicVocal',
                  className:
                    'cms-block-workspace-workspace-1-workspace-image-mention__optionImage',
                })
              )}
              <span>{item.reference.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
